import type { ImageView } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { Asset, AssetTileVariant } from '@vizit/molecules';
import { isHeroUnscored } from '@vizit/utils';

/**
 * Individual collection asset component with hover actions
 */
interface CollectionAssetProps {
  image: ImageView;
  onAssetClick?: (id: string) => void;
  onRemoveImageDialogOpen?: (id: string) => void;
  /**
   * Whether the asset is favorited for the workspace
   * @default false
   */
  isFavorite?: boolean;
  /**
   * Whether favoriting is unavailable to this user
   * @default false
   */
  isFavoriteDisabled?: boolean;
  /**
   * Called when the favorite toggle is pressed. When absent the action is not rendered.
   */
  onToggleFavorite?: () => void;
  /**
   * Multi-select for this tile (BLU-348). When absent the tile has no checkbox.
   */
  selection?: {
    isSelected: boolean;
    /** Something in the grid is selected, so every checkbox stays visible. */
    isSelectionMode: boolean;
    onSelectedChange: (options: { shiftKey: boolean }) => void;
  };
}

function CollectionAsset(props: CollectionAssetProps) {
  const { image, onAssetClick, onRemoveImageDialogOpen } = props;
  const { isFavorite = false, isFavoriteDisabled = false, onToggleFavorite, selection } = props;
  const showClassification = useFeatureFlag(FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION, false);

  const score = image.display_score ?? null;
  // The server flips `score_status` to 'SCORED' once all score fields have
  // landed. Use that as the authoritative pending signal so we don't flag
  // in-flight uploads as "Unscored" between S3 upload finishing and scoring
  // completing.
  const isPending = !image.url || image.score_status !== 'SCORED';

  return (
    <Asset
      onPress={() => {
        onAssetClick?.(image.id);
      }}
      key={image.id}
      score={score}
      isUnscored={isHeroUnscored({ omitScore: image.omit_score, score, isPending })}
      unscoredLabel="Unscored"
      isAiGenerated={image.source === 'generated'}
      isSelected={selection?.isSelected}
    >
      {showClassification && <Asset.Label className="truncate">{image.classification ?? '\u00A0'}</Asset.Label>}
      <Asset.Image border name={image.name} src={image.url_360 ?? image.url} fallbackSrc={image.url} />
      {selection && (
        <Asset.Checkbox
          aria-label={`Select ${image.name}`}
          isSelected={selection.isSelected}
          isAlwaysVisible={selection.isSelectionMode}
          onSelectedChange={(_, options) => selection.onSelectedChange(options)}
        />
      )}
      <Asset.Actions
        variant={AssetTileVariant.Sidebar}
        onFavorite={onToggleFavorite}
        isFavorite={isFavorite}
        isFavoriteDisabled={isFavoriteDisabled}
        onDelete={onRemoveImageDialogOpen ? () => onRemoveImageDialogOpen(image.id) : undefined}
      />
    </Asset>
  );
}

export { CollectionAsset };
